import { KeyboardArrowDown, Menu as MenuIcon, Visibility } from "@mui/icons-material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import {
  Box,
  Button,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";

import RearrangeModal from "./RearrangeModal";
import { useState } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadIcon from "@mui/icons-material/Upload";

import { headerStyle } from "./question.style";
import ImportQuestionsModal from "./ImportQuestionsModal";
import ImportQuestionsDrawer from "./ImportQuestionsDrawer";
import BackButton from "../Common/UI/BackButton";
import BulkDependencyModal from "./BulkDependencyModal";
import BulkRemoveConfirmModal from "./DropArea/BulkRemoveConfirmModal";
import {
  clearQuestionSelection,
  selectAllQuestions,
  selectSelectedQuestionIds,
} from "../../Redux/reducers/questionSlice";

const LoaderStyle: SxProps = {
  mr: "50px",
};

const QBHeader = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const displayLogic = useAppSelector((state) => state.study.displayLogic);

  const editable = useAppSelector((state) => state.question.editable);
  const loading = useAppSelector((state) => state.question.loading);
  const formSubmitting = useAppSelector((state) => state.question.formSubmitting);
  const questionName = useAppSelector((state) => state.question.questionName);
  const formLevel = useAppSelector((state) => state.question.formLevel);
  const length = useAppSelector((state) => state.question.questions.length);
  const selectedIds = useAppSelector(selectSelectedQuestionIds);
  const selectedCount = selectedIds.length;

  const computedDisplayLogic =
    formLevel === "platform" ? "dependencies" : displayLogic;

  const [showModal, setShowModal] = useState("");
  const [showImportDrawer, setShowImportDrawer] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkRemoveModal, setShowBulkRemoveModal] = useState(false);
  const [importMenuAnchor, setImportMenuAnchor] = useState<null | HTMLElement>(null);

  const handleBack = () => {
    navigate(-1);
  };

  const handlePreview = () => {
    navigate(`../preview?lang=en`, { relative: "path" });
  };

  const openModal = (name: string) => {
    setShowModal(name);
  };

  const closeModal = () => {
    setShowModal("");
  };

  const closeImportModal = () => {
    setShowImportModal(false);
  };

  const handleSelectAllToggle = () => {
    if (selectedCount < length) {
      dispatch(selectAllQuestions());
    } else {
      dispatch(clearQuestionSelection());
    }
  };

  return (
    <Box sx={headerStyle}>
      <BackButton onClick={handleBack} sx={{ mr: 1 }} />

      <Typography
        variant="h2"
        color="text.primary"
        sx={{
          flex: 1,
          minWidth: "0px",
        }}
        noWrap
      >
        {questionName || "Form Builder"}
      </Typography>

      <span style={{ marginLeft: "auto" }} />

      {!loading ? (
        formSubmitting ? (
          <CircularProgress size={25} sx={LoaderStyle} />
        ) : (
          <>
            <Button
              onClick={handlePreview}
              variant="outlined"
              sx={{ mr: 2 }}
              startIcon={<Visibility />}
            >
              Preview
            </Button>

            {editable && selectedCount > 0 && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleSelectAllToggle}
                  sx={{ mr: 2 }}
                >
                  {selectedCount < length ? "Select All" : "Clear Selection"}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setShowBulkRemoveModal(true)}
                  sx={{ mr: 2 }}
                >
                  Remove Selected ({selectedCount})
                </Button>
              </>
            )}

            {editable && selectedCount === 0 && (
              <>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  endIcon={<KeyboardArrowDown />}
                  sx={{ mr: 2 }}
                  onClick={(e) => setImportMenuAnchor(e.currentTarget)}
                >
                  Import Questions
                </Button>
                <Menu
                  anchorEl={importMenuAnchor}
                  open={Boolean(importMenuAnchor)}
                  onClose={() => setImportMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem onClick={() => { setImportMenuAnchor(null); setShowImportDrawer(true); }}>
                    <ListItemText>Import from File</ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => { setImportMenuAnchor(null); setShowImportModal(true); }}>
                    <ListItemText>Import from Library</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )}

            {editable &&
              length >= 2 &&
              computedDisplayLogic === "dependencies" &&
              selectedCount === 0 && (
                <Button
                  variant="outlined"
                  startIcon={<AccountTreeIcon />}
                  onClick={() => openModal("bulkDependencies")}
                  sx={{ mr: 2 }}
                >
                  Bulk Dependencies
                </Button>
              )}

            {editable && length >= 2 && selectedCount === 0 && (
              <Button
                variant="outlined"
                startIcon={<MenuIcon />}
                onClick={() => openModal("rearrangeQuestions")}
                sx={{ mr: 2 }}
              >
                Rearrange
              </Button>
            )}

            {showModal === "rearrangeQuestions" && (
              <RearrangeModal closeModal={closeModal} />
            )}
            {showModal === "bulkDependencies" && (
              <BulkDependencyModal closeModal={closeModal} />
            )}
            {showImportDrawer && (
              <ImportQuestionsDrawer
                open={showImportDrawer}
                onClose={() => setShowImportDrawer(false)}
              />
            )}
            {showImportModal && (
              <ImportQuestionsModal
                showModal={showImportModal}
                closeModal={closeImportModal}
              />
            )}
            {showBulkRemoveModal && (
              <BulkRemoveConfirmModal
                open={showBulkRemoveModal}
                onClose={() => setShowBulkRemoveModal(false)}
                questionIds={selectedIds}
              />
            )}
          </>
        )
      ) : null}
    </Box>
  );
};

export default QBHeader;
